import { useLayoutEffect, useState } from "react";

// The strip above the navbar holds exactly one notice. Everything in it is
// `position: fixed; top: 0`, and `body.has-top-system-banner` shifts the navbar
// and the page down by exactly ONE banner height - so a second banner shown at
// the same time would sit on top of the first and cover the navbar it was
// supposed to sit above.
//
// Callers therefore claim the strip instead of rendering blindly. The highest
// priority claim wins and the rest render nothing until it is released.
//
// The body class is owned here rather than by the components, because it
// describes the strip as a whole: a component that toggled it for its own
// visibility would switch it off while a different, higher-priority banner was
// still showing, and the navbar would slide up underneath it.

/**
 * Higher wins the strip. Anything the member must act on outranks anything
 * optional: someone being told they are days from losing access should not
 * have that replaced by news about a feature.
 */
export const TopBannerPriority = {
  featureAnnouncement: 10,
  mfaEnforcement: 100,
} as const;

interface Claim {
  id: string;
  priority: number;
}

let claims: Claim[] = [];
const listeners = new Set<() => void>();

// Ties go to whoever claimed first, rather than to nobody: on a strict `>`
// two banners of EQUAL priority each decide the other does not outrank them,
// both render, and they stack in the one slot - the exact thing this register
// exists to stop. A caller that has not claimed yet yields, which is the safe
// direction.
const outrankedBy = (id: string, priority: number): boolean => {
  const mine = claims.findIndex((claim) => claim.id === id);
  return claims.some(
    (claim, index) =>
      claim.id !== id &&
      (claim.priority > priority ||
        (claim.priority === priority && (mine === -1 || index < mine)))
  );
};

/**
 * Ownership as it stands right now, rather than as of the render that captured
 * it. Worth asking from an effect that fires once per appearance: on the very
 * first pass no claim is registered yet, so every banner briefly reads as the
 * owner, and a "we showed this to them" event recorded from that pass would be
 * a lie about a banner nobody ever saw.
 */
export const holdsTopBannerSlot = (
  id: string,
  priority: number,
  wants: boolean
): boolean => wants && !outrankedBy(id, priority);

const publish = () => {
  document.body.classList.toggle("has-top-system-banner", claims.length > 0);
  listeners.forEach((listener) => listener());
};

/**
 * Claim the top strip while `wants` is true, and report back whether this
 * caller is the one that should actually render. Returns false when something
 * more important holds the strip - the caller renders nothing and gets it back
 * automatically when the other banner goes away.
 */
export const useTopBannerSlot = (
  id: string,
  priority: number,
  wants: boolean
): boolean => {
  const [, bumpOnChange] = useState(0);

  // Both are layout effects, so a banner that renders before the one that
  // outranks it is corrected in the same commit, before the browser paints,
  // rather than a frame later with two banners stacked on top of each other.
  useLayoutEffect(() => {
    const onChange = () => bumpOnChange((seen) => seen + 1);
    listeners.add(onChange);
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  useLayoutEffect(() => {
    if (!wants) {
      return;
    }
    claims = claims.filter((claim) => claim.id !== id).concat({ id, priority });
    publish();
    return () => {
      claims = claims.filter((claim) => claim.id !== id);
      publish();
    };
  }, [id, priority, wants]);

  // Answered from the register as it stands right now, rather than from state
  // set by the effects below. A banner that had to wait for its own claim to
  // round-trip through an effect would render nothing on the pass where it
  // became visible and appear on the next one - a flicker on screen, and a
  // banner that is missing at the moment a test asserts on it. Reading here
  // and re-rendering on change (above) keeps the answer immediate and still
  // correct when someone else claims or releases the strip.
  return holdsTopBannerSlot(id, priority, wants);
};
